"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SharedCollectionResolver = exports.useCollectionValuesResolution = exports.useResolvedCollectionValue = void 0;
const react_1 = require("react");
const use_once_1 = require("../use-once");
const use_shared_collection_1 = require("./use-shared-collection");
function useResolvedCollectionValue(key, valueKey) {
    const [value, setValue] = (0, use_shared_collection_1.useSingleCollectionValue)(key, valueKey);
    (0, use_once_1.useOnceAsync)(() => __awaiter(this, void 0, void 0, function* () { var _a; return (_a = key.resolver) === null || _a === void 0 ? void 0 : _a.initialize(); }));
    (0, react_1.useLayoutEffect)(() => requestValue(), [valueKey, value]);
    (0, react_1.useEffect)(() => {
        var _a;
        (_a = key.resolver) === null || _a === void 0 ? void 0 : _a.resolveRequests();
    });
    function requestValue() {
        var _a;
        if (valueKey && value === undefined) {
            (_a = key.resolver) === null || _a === void 0 ? void 0 : _a.requestValue(valueKey);
        }
    }
    return [value, setValue];
}
exports.useResolvedCollectionValue = useResolvedCollectionValue;
function useCollectionValuesResolution(key, keys, values) {
    if (!key.resolver)
        return;
    (0, use_once_1.useOnceAsync)(() => __awaiter(this, void 0, void 0, function* () { var _a; return (_a = key.resolver) === null || _a === void 0 ? void 0 : _a.initialize(); }));
    (0, react_1.useLayoutEffect)(() => requestValues(), [keys, values]);
    (0, react_1.useEffect)(() => {
        var _a;
        (_a = key.resolver) === null || _a === void 0 ? void 0 : _a.resolveRequests();
    });
    function requestValues() {
        if (!keys)
            return;
        keys.filter(valueKey => values[valueKey] === undefined).forEach(valueKey => { var _a; return (_a = key.resolver) === null || _a === void 0 ? void 0 : _a.requestValue(valueKey); });
    }
}
exports.useCollectionValuesResolution = useCollectionValuesResolution;
var SharedCollectionResolverState;
(function (SharedCollectionResolverState) {
    SharedCollectionResolverState[SharedCollectionResolverState["INITIALIZING"] = 0] = "INITIALIZING";
    SharedCollectionResolverState[SharedCollectionResolverState["INITIALIZED"] = 1] = "INITIALIZED";
    SharedCollectionResolverState[SharedCollectionResolverState["NOT_INITIALIZED"] = 2] = "NOT_INITIALIZED";
})(SharedCollectionResolverState || (SharedCollectionResolverState = {}));
const { NOT_INITIALIZED, INITIALIZED, INITIALIZING } = SharedCollectionResolverState;
class SharedCollectionResolver {
    constructor(name, collection, resolver) {
        this.resolveSinglesBatch = (requests, modify) => __awaiter(this, void 0, void 0, function* () {
            const promises = requests.map((request) => __awaiter(this, void 0, void 0, function* () {
                var _a, _b;
                try {
                    const value = yield ((_b = (_a = this.resolver).resolve) === null || _b === void 0 ? void 0 : _b.call(_a, request));
                    if (value) {
                        modify.setValue(request, value);
                    }
                }
                catch (error) {
                    console.error(`Error resolving ${this.name} value for ${request}`, { error });
                }
            }));
            yield Promise.all(promises);
        });
        this.name = name;
        this.collection = collection;
        this.resolver = resolver;
        this.state = resolver.initialize ? NOT_INITIALIZED : INITIALIZED;
        this.pending = [];
        this.resolving = [];
    }
    requestValue(valueKey) {
        if (this.collection.hasKey(valueKey))
            return;
        if (this.pending.indexOf(valueKey) !== -1)
            return;
        this.pending.push(valueKey);
    }
    initialize() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            if (this.state !== NOT_INITIALIZED)
                return;
            try {
                this.state = INITIALIZING;
                yield ((_b = (_a = this.resolver).initialize) === null || _b === void 0 ? void 0 : _b.call(_a, this.collection));
            }
            catch (error) {
                console.error(`Error initializing ${this.name}`, { error });
            }
            finally {
                this.pending = this.pending.filter(request => !this.collection.hasKey(request));
                this.state = INITIALIZED;
                this.resolveRequests();
            }
        });
    }
    resolveRequests() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.pending.length === 0)
                return;
            if (this.state === INITIALIZING)
                return;
            const requests = this.popPendingRequests();
            try {
                this.resolving = this.resolving.concat(requests);
                yield this.resolveBatch(requests);
                this.resolveUnresolvedRequests(requests);
            }
            catch (error) {
                console.error(`Error resolving ${this.name} batch`, { error });
            }
            finally {
                this.removeFromResolving(requests);
            }
        });
    }
    isResolving(key) {
        if (this.state === INITIALIZING)
            return true;
        if (this.pending.includes(key))
            return true;
        if (this.resolving.includes(key))
            return true;
        return false;
    }
    valuesChanged(update) {
        if (this.resolver.valuesUpdated) {
            this.resolver.valuesUpdated(update);
        }
    }
    valuesRemoved(keys) {
        if (this.resolver.valuesRemoved) {
            this.resolver.valuesRemoved(keys);
        }
    }
    resolveBatch(requests) {
        if (this.resolver.resolveBatch) {
            // ugly type hack
            return this.resolver.resolveBatch(requests, this.collection);
        }
        else {
            return this.resolveSinglesBatch(requests, this.collection);
        }
    }
    resolveUnresolvedRequests(requests) {
        const update = {};
        let shouldUpdate = false;
        for (const request of requests) {
            if (!this.collection.hasKey(request)) {
                update[request] = null;
                shouldUpdate = true;
            }
        }
        if (shouldUpdate === true)
            this.collection.update(update);
    }
    popPendingRequests() {
        const popped = [...this.pending];
        this.pending = [];
        return popped;
    }
    removeFromResolving(requests) {
        this.resolving = this.resolving.filter(fkey => !requests.includes(fkey));
    }
}
exports.SharedCollectionResolver = SharedCollectionResolver;
//# sourceMappingURL=shared-value-resolving.js.map